import { useRef } from 'react';
import { useQueries } from '@tanstack/react-query';
import useGlynt from './useGlynt';
import { ResultsChainGaveUpError, ValidationNotReadyError } from '../../api/extraction';
import errorResponses from '../../errorResponses';

const MAX_VALIDATION_RETRIES = 3;

// validate() already polls until the API settles, so an exhausted poll is final:
// retrying it would keep the page in a loading state for several more budgets.
// A results chain that gave up stays given up until it is rerun.
// Other failures (network, 5xx) keep React Query's default retry behaviour.
export const retryValidationQuery = (failureCount: number, error: unknown): boolean =>
  !(error instanceof ValidationNotReadyError) &&
  !(error instanceof ResultsChainGaveUpError) &&
  failureCount < MAX_VALIDATION_RETRIES;

/**
 * The error toast for a set of validation queries: the API's message when a
 * results chain gave up, the generic failure otherwise, and none when nothing
 * failed or only a poll budget ran out (that state has its own retry affordance).
 */
export const validationErrorToastMessage = (
  queries: { isError: boolean; error: unknown }[]
): string | undefined => {
  const errors = queries.filter((query) => query.isError).map((query) => query.error);
  const gaveUp = errors.find((error) => error instanceof ResultsChainGaveUpError);
  if (gaveUp) {
    return (gaveUp as ResultsChainGaveUpError).message;
  }
  return errors.some((error) => !(error instanceof ValidationNotReadyError))
    ? errorResponses.failedToDataValidation
    : undefined;
};

export function useDataValidation(
  enabled: boolean = false,
  visibleExtractionIds: string[],
  includeTransformed: boolean = false,
  forceValidation: boolean = false,
  includeExtracted: boolean = true,
  includeSchematized: boolean = false
) {
  const glynt = useGlynt();
  const forceValidationRef = useRef(forceValidation);
  forceValidationRef.current = forceValidation;
  const includeSchematizedRef = useRef(includeSchematized);
  includeSchematizedRef.current = includeSchematized;

  const buildQuery = visibleExtractionIds.map((extractionId: string) => {
    return {
      queryKey: [
        'extraction-validation',
        extractionId,
        includeExtracted,
        includeTransformed,
      ],
      queryFn: async () => {
        const glyntInstance = await glynt;
        return glyntInstance.extraction.validate(
          extractionId,
          includeExtracted,
          includeTransformed,
          forceValidationRef.current,
          includeSchematizedRef.current
        );
      },
      enabled: enabled,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: retryValidationQuery,
    };
  });

  const allQueries = useQueries({
    queries: buildQuery
  });

  return allQueries;
}
